"use client"

import { Check, ArrowRight, Star, Users } from "lucide-react"
import { motion } from "framer-motion"
import { useAuth0 } from "@auth0/auth0-react"

export default function PricingSection() {
  const { loginWithRedirect } = useAuth0()

  const handleButtonClick = () => {
    loginWithRedirect()
  }

  const features = [
    "Fully automated onboarding. Just enter your URL and press 'Go.'",
    "TrendHopper will research your site, audience, and keywords.",
    "Weekly content plans and article production.",
    "Approve, decline, or moderate articles as needed.",
    "Automatic internal linking for articles and site pages.",
  ]

  return (
    <section
      id="pricing"
      className="bg-gradient-to-b from-slate-900 via-slate-900 to-slate-900/90 text-white py-32 px-6 relative overflow-hidden"
    >
      {/* Animated background gradient */}

      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-radial-gradient" />

      <div className="max-w-6xl mx-auto text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          <span className="inline-flex items-center gap-2 bg-slate-800 text-slate-200 text-sm font-medium px-4 py-2 rounded-full mb-4 border border-slate-700/50 shadow-lg">
            <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse" />
            Limited Time Offer
          </span>

          <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight tracking-tight">
            Perhaps the{" "}
            <span className="relative">
              <span className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-rose-500 blur-lg opacity-50" />
              <span className="relative bg-gradient-to-r from-orange-500 to-rose-500 bg-clip-text text-transparent px-2">
                best ROI
              </span>
            </span>{" "}
            on the market
          </h2>

          {/* Social proof */}
        </motion.div>

        <div className="grid gap-8 lg:grid-cols-2 items-start mt-16">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="h-full"
          >
            <div className="group bg-slate-800/80 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-8 h-full flex flex-col hover:border-orange-500/50 transition-colors duration-300">
              <div className="flex-grow flex flex-col justify-between">
                <div className="space-y-6">
                  {/* Star rating */}
                  <div className="flex justify-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-orange-500 text-orange-500" />
                    ))}
                  </div>

                  <p className="text-2xl font-medium text-orange-400 leading-relaxed">
                    "It's like having a full-time SEO team working for you 24/7."
                  </p>

                  <div className="space-y-4">
                    <p className="text-lg text-slate-200">
                    Used to help businesses scale effortlessly, increasing efficiency and results.
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed">
                      With a mission to pioneer AI-driven solutions that maximize productivity and results, we've helped
                      users generate incredible outcomes.
                    </p>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                  <div className="space-y-1">
                    <p className="text-4xl font-bold tracking-tight">
                      $9.99
                      <span className="text-lg font-normal text-slate-400">/month</span>
                    </p>
                    <p className="text-sm text-slate-400">No credit card required</p>
                  </div>

                  <motion.button
                    onClick={handleButtonClick}
                    className="group relative w-full sm:w-auto"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-rose-500 rounded-lg blur opacity-50 group-hover:opacity-75 transition duration-200" />
                    <div className="relative flex items-center justify-center bg-gradient-to-r from-orange-500 to-rose-500 text-white font-semibold py-3 px-8 rounded-lg">
                      <span>Drive SEO Traffic</span>
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="h-full"
          >
            <div className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8 h-full flex flex-col">
              <h3 className="text-2xl font-bold text-white mb-8">What you get:</h3>

              <ul className="space-y-6 flex-grow">
                {features.map((feature, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="flex items-start gap-4 group"
                  >
                    <span className="rounded-full p-1.5 bg-gradient-to-r from-orange-500/20 to-rose-500/20 text-rose-400 group-hover:from-orange-500/30 group-hover:to-rose-500/30 transition-all duration-300">
                      <Check className="w-4 h-4" />
                    </span>
                    <span className="text-lg text-slate-300 group-hover:text-white transition-colors">{feature}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
